import { invoke } from "../invoke";
import { displayFileName } from "../fileNames";
import { isDefaultSceneFileName } from "../zwConfig";
import { sceneListName } from "../sceneLabel";
import {
  normalizePayload,
  type EditorPayload,
  type RawEditorPayload,
} from "./core";

import type { UnplacedItem, UnplacedScene } from "../outlineEntityTypes";

export type { UnplacedItem, UnplacedScene };

/** No extension, and a default-named parked scene (`scene-a1b2c.md`) just
 * reads as "Scene" — there's no chapter position to number it against once
 * it's out of the manuscript. Shared by the Storyboard's Unplaced tray and
 * the Scratchpad tab's note list — both render the same underlying entries. */
export function scenelikeDisplayName(
  name: string,
  kind?: "scene" | "chapter",
): string {
  if (kind === "scene" && isDefaultSceneFileName(name)) return "Scene";
  return displayFileName(name);
}

/** A parked nameless scene is labelled by its synopsis (the same words the
 * Storyboard card showed), falling back to "Untitled scene". */
export function unplacedItemDisplayName(item: UnplacedItem): string {
  if (item.kind === "scene" && isDefaultSceneFileName(item.name)) {
    return sceneListName({ file: item.name, synopsis: item.synopsis });
  }
  return item.title.length > 0
    ? item.title
    : scenelikeDisplayName(item.name, item.kind);
}

/** The synopsis line under a parked item, unless it's already the label. */
export function unplacedItemShowsSynopsis(item: UnplacedItem): boolean {
  return (
    item.synopsis.trim() !== "" &&
    (item.kind === "scene" && isDefaultSceneFileName(item.name)) === false
  );
}

export async function listProjectUnplaced(
  rootPath: string,
): Promise<UnplacedItem[]> {
  return invoke<UnplacedItem[]>("list_project_unplaced", { rootPath });
}

export async function parkScene(
  rootPath: string,
  folder: string,
  sceneFile: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("park_scene", {
    rootPath,
    folder,
    sceneFile,
  });
  return normalizePayload(raw);
}

export async function parkChapter(
  rootPath: string,
  folder: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("park_chapter", {
    rootPath,
    folder,
  });
  return normalizePayload(raw);
}

export async function unparkScene(
  rootPath: string,
  name: string,
  toFolder: string,
  toSceneFile: string | null,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("unpark_scene", {
    rootPath,
    name,
    toFolder,
    toSceneFile,
  });
  return normalizePayload(raw);
}

export async function unparkChapter(
  rootPath: string,
  name: string,
  atIndex: number,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("unpark_chapter", {
    rootPath,
    name,
    atIndex,
  });
  return normalizePayload(raw);
}

/** Opens a parked scene or chapter file for editing in the Scratchpad tab.
 * `name` is relative to `Notes/` — a top-level parked scene's filename
 * (`"scene-a1b2c.md"`), or `"<chapter name>/<file>"` for a file one level
 * inside a parked chapter (its `index.zwdoc`, or one of its scenes). */
export async function openParkedItem(
  rootPath: string,
  name: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("open_parked_item", {
    rootPath,
    name,
  });
  return normalizePayload(raw);
}

export async function updateParkedSceneSynopsis(
  rootPath: string,
  name: string,
  synopsis: string,
): Promise<void> {
  await invoke("update_parked_scene_synopsis", {
    rootPath,
    name,
    title: "",
    synopsis,
  });
}

/** Renames a top-level parked scene or a parked chapter (`name`/`toName` are
 * both bare, one level under `Notes/` — renaming a chapter's individual
 * scenes isn't offered from the Scratchpad tab). */
export async function renameParkedItem(
  rootPath: string,
  name: string,
  toName: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("rename_parked_item", {
    rootPath,
    name,
    toName,
  });
  return normalizePayload(raw);
}

/** Deletes a top-level parked scene (file) or a parked chapter (folder, with
 * every scene inside it). */
export async function deleteParkedItem(
  rootPath: string,
  name: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("delete_parked_item", {
    rootPath,
    name,
  });
  return normalizePayload(raw);
}
